import React from 'react';
import { motion } from 'framer-motion';
import { Play, ArrowRight, Sparkles, Music } from 'lucide-react';

const Hero = () => {
  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24"
      style={{
        background: 'radial-gradient(ellipse at bottom, #0f172a 0%, #090e1a 100%)',
      }}
    >
      {/* Animated background gradient */}
      <motion.div 
        className="absolute inset-0 overflow-hidden opacity-30"
        initial={{ backgroundPosition: '0% 50%' }}
        animate={{
          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
        }}
        transition={{
          duration: 15,
          ease: 'linear',
          repeat: Infinity,
        }}
        style={{
          background: 'linear-gradient(45deg, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #10b981, #3b82f6)',
          backgroundSize: '400% 400%',
          filter: 'blur(100px)',
        }}
      />

      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-5" 
        style={{
          backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} 
      />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <motion.div 
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 mr-2"></span>
                <span className="text-sm font-medium bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                  Discover Your Signature Sound
                </span>
              </motion.div>

              <motion.h1 
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                Find Your Perfect <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Guitar Tone</span>
              </motion.h1>
              
              <motion.p 
                className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl opacity-90"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                Discover the gear behind legendary tones and create your own signature sound with our interactive tone finder and rig builder.
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <motion.button 
                  className="group relative overflow-hidden px-8 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 30px -5px rgba(99, 102, 241, 0.5)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Start Exploring</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                
                <motion.button 
                  className="group relative overflow-hidden px-6 py-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 text-white font-medium flex items-center gap-2"
                  whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <Play className="w-3 h-3 text-white fill-current ml-0.5" />
                  </div>
                  <span>Watch Demo</span>
                </motion.button>
              </motion.div>
              
              <motion.div 
                className="flex flex-wrap gap-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                {[
                  { value: '500+', label: 'Rigs' },
                  { value: '10K+', label: 'Tones' },
                  { value: '50+', label: 'Artists' },
                  { value: '1M+', label: 'Users' }
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
            
            {/* Right content */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              {/* Main card */}
              <motion.div 
                className="relative bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl rounded-2xl p-1 border border-white/10 overflow-hidden"
                whileHover={{ boxShadow: '0 20px 50px -10px rgba(99, 102, 241, 0.15)' }}
              >
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800/80 rounded-xl p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-semibold text-white">Featured Rig</h3>
                    <div className="flex items-center text-xs text-gray-400">
                      <Sparkles className="w-4 h-4 mr-1 text-yellow-400" />
                      Popular
                    </div>
                  </div>
                  
                  <div className="mb-6 aspect-video bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <Music className="w-12 h-12 mx-auto text-blue-400 mb-3" />
                      <span className="text-sm text-gray-400">Rig Preview</span>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-medium text-white">
                        Classic Rock Legend
                      </h4>
                      <p className="text-sm text-gray-400">
                        Based on the iconic tones of rock history
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-white/5">
                      <div className="text-sm">
                        <div className="text-gray-400">Guitar</div>
                        <div className="text-white">Les Paul '59</div>
                      </div>
                      <div className="text-sm text-right">
                        <div className="text-gray-400">Amp</div>
                        <div className="text-white">Marshall Plexi</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Floating elements */}
              <motion.div 
                className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-transparent rounded-2xl -z-10"
                animate={{
                  y: [0, 15, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  repeatType: 'reverse',
                  ease: 'easeInOut',
                }}
              />
              <motion.div 
                className="absolute -bottom-8 -right-8 w-40 h-40 bg-gradient-to-tr from-purple-500/15 to-transparent rounded-3xl -z-10"
                animate={{
                  y: [0, -20, 0],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  repeatType: 'reverse',
                  ease: 'easeInOut',
                  delay: 1,
                }}
              />
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <motion.div 
          className="text-sm text-gray-400 mb-3 flex items-center gap-2"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatType: 'loop' }}
        >
          <span>Scroll to explore</span>
          <ArrowRight className="w-4 h-4 transform rotate-90" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
